"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import { RiArrowRightSLine, RiArrowRightUpLine, RiApps2Line } from "react-icons/ri";
import { Sheet, useViewStack } from "@dionatanbig2be/big2be-ui";
import { Icon } from "@/shared/components/Icon";
import { MediaImage } from "@/shared/components/MediaImage";
import type { CategoryItem } from "../../types/category-panel";
import { lastOf, resolveCategoryPath } from "../../lib/resolve-category-path";
import { CategoryPanelProducts } from "./CategoryPanelProducts";
import { CategoryPanelVideo } from "./CategoryPanelVideo";

interface CategoryPanelMobileProps {
    categories: CategoryItem[];
    onClose: () => void;
    /** Fecha o estado sem qualquer side-effect de history — usado nos Links
     * de saída (folha), que já disparam a navegação real no mesmo clique.
     * Quando `onClose` tiver side-effect de history (ver
     * useModalHistoryEntry.close, que faz history.back()), chamá-lo no
     * mesmo evento de clique de um Link competia com a navegação real e
     * podia desfazê-la (a página abria e voltava instantaneamente). Sem
     * essa prop, cai no próprio `onClose`. */
    onNavigateAway?: () => void;
    /** Mostra um spinner no corpo (dados ainda carregando) — mesma casca, sem remontar. */
    loading?: boolean;
    /** Mensagem de erro no corpo (falha ao carregar). */
    error?: boolean;
    /** Repassado ao Sheet — false dispara a saída animada. */
    open?: boolean;
    /**
     * Ids da raiz até a categoria em que o painel já abre (aba da barra no
     * celular). Nesse nível o voltar fecha o painel — ele não tem X, e voltar
     * para a lista de departamentos obrigaria a um segundo toque. Sem ela, abre na raiz.
     */
    initialPath?: string[];
}

const ROOT_VIEW = "root";

const ROW_CLASS =
    "flex w-full items-center gap-4 px-5 py-5 text-left transition-colors duration-150 hover:bg-base-200/50 active:bg-base-200";

interface CategoryRowProps {
    label: string;
    icon: ReactNode;
    /** Presente → linha entra na categoria (chevron). Ausente → link de saída (↗). */
    onDrill?: () => void;
    href?: string;
    onNavigate?: () => void;
}

/** Uma única definição de linha para todos os níveis do drill-down. */
function CategoryRow({ label, icon, onDrill, href, onNavigate }: CategoryRowProps) {
    const content = (
        <>
            <span className="flex w-6 shrink-0 items-center justify-center text-primary">{icon}</span>
            <span className="flex-1 text-lg text-base-content/80">{label}</span>
            {onDrill ? (
                <RiArrowRightSLine size={22} className="shrink-0 text-primary" />
            ) : (
                <RiArrowRightUpLine size={22} className="shrink-0 text-primary" />
            )}
        </>
    );

    return (
        <li className="border-b border-base-200/70">
            {onDrill ? (
                <button type="button" onClick={onDrill} className={ROW_CLASS}>
                    {content}
                </button>
            ) : (
                <Link href={href ?? "#"} onClick={onNavigate} className={ROW_CLASS}>
                    {content}
                </Link>
            )}
        </li>
    );
}

/**
 * Fluxo mobile compartilhado por Classic e Modern: navegação drill-down de
 * profundidade arbitrária (categoria → subcategoria → …), sem accordion.
 *
 * Navegação em pilha via `useViewStack` (ids de categoria); a categoria ativa
 * é resolvida andando pela árvore com `resolveCategoryPath`. Em cada nível
 * mostramos "Ver todas" (link p/ a categoria atual) e, se ela tiver
 * `bannerUrl`, um banner promocional ancorado na base (a listagem divide a
 * altura com ele e rola no próprio espaço).
 *
 * Fechar o painel (nível raiz) delega a `onClose` — o chamador decide COMO
 * (ex.: HeaderCategoryPanelModal conecta isso a useModalHistoryEntry, pro
 * voltar nativo do aparelho funcionar; CategoryPanelClassic/Modern usam um
 * estado local simples). Navegar entre níveis internos do drill-down é só
 * estado local (`useViewStack`), sem envolver history.
 */
export function CategoryPanelMobile({
    categories,
    onClose,
    onNavigateAway,
    loading = false,
    error = false,
    open = true,
    initialPath,
}: CategoryPanelMobileProps) {
    const views = useViewStack(ROOT_VIEW);

    useLayoutEffect(() => {
        if (!initialPath?.length) return;
        views.reset();
        initialPath.forEach(views.push);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Direção da última navegação entre níveis, para animar a lista no sentido
    // certo (avançar → entra da direita; voltar → entra da esquerda).
    const [dir, setDir] = useState<"forward" | "back">("forward");

    // O 1º id da pilha é o sentinela ROOT_VIEW, que não existe na árvore.
    const path = resolveCategoryPath(categories, views.stack.slice(1));
    const current = lastOf(path);
    const items = current?.children ?? categories;
    // Path completo (raiz → atual) exibido como subtítulo no header, igual ao
    // padrão nome+status do ChatHeader — só faz sentido com mais de 1 nível
    // (o nível 1 já repete o próprio título).
    const breadcrumb = path.length > 1 ? path.map((cat) => cat.label).join(" > ") : undefined;
    // Mesma regra do desktop (CategoryDetailCard): banner com fallback pra imageUrl.
    const banner = current
        ? (current.panelImageMobileUrl ?? current.panelImageDesktopUrl ?? current.bannerUrl ?? current.imageUrl)
        : undefined;
    // Vídeo ganha da arte (mesma regra do CategoryDetailCard).
    const video = current ? (current.panelVideoMobileUrl ?? current.panelVideoDesktopUrl) : undefined;
    // A vitrine ocupa o lugar da arte na base do painel: o portal grava uma ou outra.
    const panelProductIds = current?.panelProductIds?.length ? current.panelProductIds : undefined;

    // Painel fechado (por qualquer via) → reseta o drill-down, pra não
    // reabrir preso no mesmo nível de uma sessão anterior (o componente
    // nunca desmonta entre aberturas — só alterna `open` pra animar a saída).
    useEffect(() => {
        if (!open) views.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Na raiz (ou na categoria em que o painel abriu), o voltar fecha o painel;
    // em um nível interno, apenas despilha a categoria — 100% estado local.
    const openedAtDepth = 1 + (initialPath?.length ?? 0);
    const goBack = useCallback(() => {
        if (views.stack.length <= openedAtDepth) {
            onClose();
        } else {
            setDir("back");
            views.back();
        }
    }, [onClose, views, openedAtDepth]);

    // Fechar via link (clique numa folha): só fecha o estado — a navegação
    // do Link já leva pra nova URL nesse mesmo clique.
    const closeAll = useCallback(() => {
        (onNavigateAway ?? onClose)();
    }, [onNavigateAway, onClose]);

    return (
        <Sheet open={open} onBack={goBack}>
            <Sheet.Header
                title={
                    <span className="flex min-w-0 flex-col">
                        <span className="flex items-center gap-2">
                            {current?.label ?? "Categorias"}
                            {!loading && !error && items.length > 0 && (
                                <span className="badge badge-primary badge-soft badge-sm shrink-0 rounded-full font-semibold">
                                    {items.length > 99 ? "99+" : items.length}
                                </span>
                            )}
                        </span>
                        {breadcrumb && (
                            <span className="truncate text-xs font-normal text-base-content/60">{breadcrumb}</span>
                        )}
                    </span>
                }
            />
            <Sheet.Body className="gap-0">
                {loading || error ? (
                    <div className="flex flex-1 items-center justify-center">
                        {error ? (
                            <p className="text-sm text-base-content/60">Não foi possível carregar as categorias.</p>
                        ) : (
                            <span className="loading loading-dots loading-md text-primary" />
                        )}
                    </div>
                ) : (
                    <>
                        {/* key = profundidade → remonta e reanima a lista a cada troca de nível */}
                        <ul
                            key={views.stack.length}
                            className={`min-h-0 flex-1 overflow-y-auto motion-reduce:animate-none ${
                                dir === "forward"
                                    ? "animate-category-level-forward"
                                    : "animate-category-level-back"
                            }`}
                        >
                            {/* Dentro de uma categoria: "Ver todas" leva à própria categoria atual */}
                            {current && (
                                <CategoryRow
                                    label="Ver todas"
                                    icon={<RiApps2Line size={22} />}
                                    href={current.href}
                                    onNavigate={closeAll}
                                />
                            )}

                            {items.map((cat) => {
                                // Só faz drill se tiver filhos; senão é link de saída.
                                const canDrill = !!cat.children?.length;
                                return (
                                    <CategoryRow
                                        key={cat.id}
                                        label={cat.label}
                                        icon={<Icon name={cat.icon} size={22} />}
                                        onDrill={
                                            canDrill
                                                ? () => {
                                                      setDir("forward");
                                                      views.push(cat.id);
                                                  }
                                                : undefined
                                        }
                                        href={cat.href}
                                        onNavigate={closeAll}
                                    />
                                );
                            })}
                        </ul>

                        {current && panelProductIds ? (
                            <CategoryPanelProducts
                                productIds={panelProductIds}
                                layout="strip"
                                onNavigate={closeAll}
                                className="shrink-0 border-t border-base-200 p-4"
                            />
                        ) : (
                            current &&
                            (video || banner) && (
                                <div className="shrink-0 border-t border-base-200 p-4">
                                    <Link
                                        href={current.href}
                                        onClick={closeAll}
                                        className="block transition-opacity duration-150 active:opacity-80"
                                    >
                                        <div className="relative mx-auto aspect-34/16 w-full max-w-xs overflow-hidden rounded-xl bg-base-200">
                                            {video ? (
                                                <CategoryPanelVideo path={video} />
                                            ) : (
                                                <MediaImage
                                                    path={banner}
                                                    alt={`Promoção ${current.label}`}
                                                    fill
                                                    sizes="152px"
                                                    className="object-cover"
                                                />
                                            )}
                                        </div>
                                    </Link>
                                    <Link
                                        href={current.href}
                                        onClick={closeAll}
                                        className="btn btn-primary btn-md mx-auto mt-3 flex w-full max-w-xs"
                                    >
                                        Visualizar todas
                                    </Link>
                                </div>
                            )
                        )}
                    </>
                )}
            </Sheet.Body>
        </Sheet>
    );
}
